/**
 * PWA install + service-worker registration.
 *
 * - Registers /sw.js (Vite serves public/sw.js verbatim).
 * - Captures `beforeinstallprompt` so we can present the install CTA at a
 *   user-relevant moment (after first solve; never on first paint).
 */
let deferredPrompt: BeforeInstallPromptEvent | null = null;

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function initPwa(): void {
  if ('serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => null);
    });
  }
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
  });
}

/** Returns true if the install prompt is available right now. */
export function canInstall(): boolean {
  return deferredPrompt !== null;
}

/** Trigger the install prompt; returns outcome. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return choice.outcome;
}
